import { APIFetchKryptonForComms, RequestHeaders } from "@certa/network";
import { useQuery } from "react-query";
import { useCommsNewEndpoints } from "@certa/common/toggles";

type ObjectNameRawResponse = {
  step?: {
    [key: string]: {
      name: string;
      step_group_id: number;
      step_group_name: string;
      workflow_id: number;
      workflow_name: string;
    };
  };
  field?: {
    [key: string]: {
      body: string;
      step_id: number;
      step_name: string;
      step_group_id: number;
      step_group_name: string;
      workflow_id: number;
      workflow_name: string;
    };
  };
};

type CommonObjectDetails = {
  stepGroupId: string;
  stepGroupName: string;
  workflowId: string;
  workflowName: string;
  stepId: string;
  stepName: string;
};

type StepDetails = CommonObjectDetails & {
  type: "step";
};

type FieldDetails = CommonObjectDetails & {
  type: "field";
  fieldId: string;
  fieldName: string;
};

export const isFieldDetails = (
  details: StepDetails | FieldDetails
): details is FieldDetails => details.type === "field";

type ObjectPathDetails = {
  step: Record<string, StepDetails>;
  field: Record<string, FieldDetails>;
};

type ObjectNamePayload = {
  step?: string[];
  field?: string[];
};

const objectNamesModelCreator = (
  response: ObjectNameRawResponse
): ObjectPathDetails => {
  const transformedResponse: ObjectPathDetails = {
    step: {},
    field: {}
  };

  if (response.step) {
    Object.entries(response.step).forEach(([key, value]) => {
      transformedResponse.step[key] = {
        type: "step",
        stepId: key,
        stepName: value.name,
        stepGroupId: value.step_group_id.toString(),
        stepGroupName: value.step_group_name,
        workflowId: value.workflow_id.toString(),
        workflowName: value.workflow_name
      };
    });
  }

  if (response.field) {
    Object.entries(response.field).forEach(([key, value]) => {
      transformedResponse.field[key] = {
        type: "field",
        fieldId: key,
        fieldName: value.body,
        stepId: value.step_id.toString(),
        stepName: value.step_name,
        stepGroupId: value.step_group_id.toString(),
        stepGroupName: value.step_group_name,
        workflowId: value.workflow_id.toString(),
        workflowName: value.workflow_name
      };
    });
  }

  return transformedResponse;
};

const getObjectNamesFromKrypton = async (payload: ObjectNamePayload) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      data: payload
    })
  };

  return APIFetchKryptonForComms<ObjectNameRawResponse>(
    `comms/object_names/`,
    requestOptions
  ).then(objectNamesModelCreator);
};

const useObjectNamesFromKrypton = (payload: ObjectNamePayload) => {
  const isCommsNewEndpointEnabled = useCommsNewEndpoints();

  return useQuery<ObjectPathDetails>(
    ["getObjectNamesFromKrypton", payload],
    () => getObjectNamesFromKrypton(payload),
    {
      refetchOnWindowFocus: false,
      enabled: isCommsNewEndpointEnabled
    }
  );
};

export { getObjectNamesFromKrypton, useObjectNamesFromKrypton };
export type { ObjectPathDetails, StepDetails, FieldDetails };
